import { ContactDetailsInfo } from '@store/patientStore';
import { IntegrationModes } from '@utils/enums/IntegrationModes.enum';
import { StaticValues } from '@utils/enums/StaticValues.enum';
import { CustomFields } from '@utils/interfaces';
import {
  MarketingConsentField,
  MarketingConsentFieldType,
  MarketingConsentFormValues,
} from './MarketingConsentForm.interface';

export const getMarketingConsentFields = (
  customFieldsMarketing: CustomFields | undefined,
  integrationMode?: string,
): MarketingConsentField[] => {
  const fields =
    customFieldsMarketing?.children.map((field, originalIndex) => ({
      field,
      originalIndex,
    })) ?? [];

  return fields
    .filter(({ field }) => {
      if (integrationMode === IntegrationModes.STANDALONE) {
        return field.id !== 'EMAIL';
      }

      return true;
    })
    .map(({ field, originalIndex }) => {
      const isCustomMessage =
        originalIndex === 3 || field.id === 'customMessage';

      return {
        config: field,
        id: field.id,
        originalIndex,
        valueKey: `field${originalIndex + 1}`,
        type: isCustomMessage
          ? MarketingConsentFieldType.MESSAGE
          : MarketingConsentFieldType.CONSENT,
      };
    });
};

export const hasVisibleMarketingConsentFields = (
  fields: MarketingConsentField[],
) =>
  fields.some(
    field => field.config.status !== StaticValues.FIELD_STATUS_HIDDEN,
  );

export const getMarketingConsentDefaultValues = (
  fields: MarketingConsentField[],
  contactsDetails?: ContactDetailsInfo,
): MarketingConsentFormValues => {
  const currentValues = contactsDetails?.storeCustomFieldsMarketing ?? {};

  const storeCustomFieldsMarketing = fields.reduce<Record<string, boolean>>(
    (acc, field) => {
      if (field.type !== MarketingConsentFieldType.CONSENT) {
        return acc;
      }

      const savedValue =
        currentValues[field.valueKey] ??
        (field.id ? currentValues[field.id] : undefined);
      const value =
        typeof savedValue === 'boolean'
          ? savedValue
          : !!field.config.defaultValue;

      acc[field.valueKey] = value;

      if (field.id) {
        acc[field.id] = value;
      }

      return acc;
    },
    {},
  );

  return {
    contactsDetails: {
      storeCustomFieldsMarketing,
    },
  };
};

export const getMarketingConsentLabel = (
  field: MarketingConsentField,
  isBranded: boolean,
  t: (key: string) => string,
) => {
  if (isBranded) {
    return t(`contactDetailsPage.${field.config.label}`) || field.config.label;
  }

  return field.config.label;
};

export const getMarketingConsentMessage = (
  field: MarketingConsentField,
  isBranded: boolean,
  t: (key: string) => string,
) => {
  const message = field.config.label || field.config.labelTextOnly || '';

  if (isBranded) {
    return t(`contactDetailsPage.${message}`) || message;
  }

  return message;
};
